/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: utils\extractOptions.ts
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the Provisioning Templates module.
────────────────────────────────────────────────────────────────*/

export interface DssKeysInfo {
    count: number;
    types: { value: string; label: string }[];
    maxAccounts: number;
}

/**
 * Result type for Expansion Module info extraction
 */
export interface ExpansionModuleInfo {
    moduleCount: number;
    keysPerModule: number;
    types: { value: string; label: string }[];
    maxAccounts: number;
}

/**
 * Helper to extract DSS Keys information from model
 * @param keys - the keys JSONB field
 * @param accounts - the accounts JSONB field
 */
export function extractDssKeysInfo(keys: unknown, accounts?: unknown): DssKeysInfo {
    if (!keys) return { count: 0, types: [], maxAccounts: 1 };

    try {
        const keysObj = typeof keys === 'string' ? JSON.parse(keys) : keys as Record<string, unknown>;
        const count = (keysObj as Record<string,number>).dss_keys || 0;

        // Extract maxAccounts from the accounts JSONB field
        let maxAccounts = 1;
        if (accounts) {
            const accountsObj = typeof accounts === 'string' ? JSON.parse(accounts) : accounts as Record<string, unknown>;
            maxAccounts = (accountsObj as Record<string,number>)?.count || 1;
        }

        // Extract types from fields.type.values
        let types: { value: string; label: string }[] = [];
        const keysObjR = keysObj as Record<string,unknown>;
        const fieldsR = keysObjR?.fields as Record<string,unknown>|undefined;
        const typeValues = (fieldsR?.type as Record<string,unknown>)?.values;

        if (Array.isArray(typeValues)) {
            // New format: [{ key, value, selected }, ...]
            types = (typeValues as Array<Record<string,unknown>>)
                .filter((item) => item['selected'] !== false)
                .map((item) => ({
                    value: String(item['key'] ?? item['value'] ?? ''),
                    label: String(item['key'] ?? item['value'] ?? '')
                }));
        } else if (typeValues && typeof typeValues === 'object') {
            // Legacy format: { 'BLF': '1', 'Speed Dial': '2', ... }
            types = Object.keys(typeValues as object).map(typeName => ({
                value: typeName,
                label: typeName
            }));
        } else if (Array.isArray((keysObjR as Record<string,unknown>)?.['supported_types'])) {
            // Simple format: ['BLF', 'Speed Dial', ...]
            types = ((keysObjR as Record<string,unknown>)['supported_types'] as string[]).map((typeName) => ({
                value: typeName,
                label: typeName
            }));
        }

        // Sort alphabetically
        types.sort((a, b) => a.label.localeCompare(b.label));

        return { count, types, maxAccounts };
    } catch (e) {
        console.warn('[DSS Keys] Failed to parse keys field:', e);
        return { count: 0, types: [], maxAccounts: 1 };
    }
}

/**
 * Helper to extract Expansion Module information from model
 */
export function extractExpansionModuleInfo(expansionModuleKeys: unknown, accounts?: unknown): ExpansionModuleInfo {
    if (!expansionModuleKeys) return { moduleCount: 0, keysPerModule: 0, types: [], maxAccounts: 1 };

    try {
        const expObj = (typeof expansionModuleKeys === 'string' ? JSON.parse(expansionModuleKeys) : expansionModuleKeys) as Record<string, unknown>;
        const moduleCount = (expObj as Record<string,number>).count || 0;
        // The structure is: { count: N, keys: { dss_keys: M }, models: [] }
        const expKeys = expObj.keys as Record<string,number>|undefined;
        const keysPerModule = expKeys?.dss_keys || (expObj as Record<string,number>).keysPerModule || (expObj as Record<string,number>).keys_per_module || 0;

        // Extract maxAccounts from the accounts JSONB field
        let maxAccounts = 1;
        if (accounts) {
            const accountsObj = (typeof accounts === 'string' ? JSON.parse(accounts) : accounts) as Record<string,number>;
            maxAccounts = accountsObj?.count || 1;
        }

        // Extract types from fields.type.values (same structure as DSS keys)
        let types: { value: string; label: string }[] = [];
        const fieldsTypeValues = ((expObj?.fields as Record<string,unknown>)?.type as Record<string,unknown>)?.values
            ?? ((expKeys as unknown as Record<string,unknown>)?.fields as Record<string,unknown>);

        if (Array.isArray(fieldsTypeValues)) {
            types = (fieldsTypeValues as Array<Record<string,unknown>>)
                .filter((item) => item['selected'] !== false)
                .map((item) => ({
                    value: String(item['key'] ?? item['value'] ?? ''),
                    label: String(item['key'] ?? item['value'] ?? '')
                }));
        } else if (fieldsTypeValues && typeof fieldsTypeValues === 'object') {
            types = Object.keys(fieldsTypeValues as object).map(typeName => ({
                value: typeName,
                label: typeName
            }));
        } else if (Array.isArray((expObj as Record<string,unknown>)?.['supported_types'])) {
            types = ((expObj as Record<string,unknown>)['supported_types'] as string[]).map((typeName) => ({
                value: typeName,
                label: typeName
            }));
        }

        // Sort alphabetically
        types.sort((a, b) => a.label.localeCompare(b.label));

        return { moduleCount, keysPerModule, types, maxAccounts };
    } catch (e) {
        console.warn('[Expansion Modules] Failed to parse expansion_module_keys field:', e);
        return { moduleCount: 0, keysPerModule: 0, types: [], maxAccounts: 1 };
    }
}

/**
 * Helper function to extract timezone options from model
 * Supports three formats:
 * 1. New format with selected: [{ key, label, value, selected }, ...] where value is GMT offset
 * 2. Array format: [{"Africa/Abidjan":"Abidjan|0"}, ...]
 * 3. Object format: {"Africa/Abidjan": "value1", ...}
 */
/**
 * Helper function to extract timezone options from model
 * Supports three formats:
 * 1. New format with selected: [{ key, label, value, selected }, ...] where value is GMT offset
 * 2. Array format: [{"Africa/Abidjan":"Abidjan|0"}, ...]
 * 3. Object format: {"Africa/Abidjan": "value1", ...}
 */
export function extractTimezoneOptions(timezone: unknown): { value: string, label: string }[] {
    if (!timezone) return [];
    try {
        const tzObj = typeof timezone === 'string' ? JSON.parse(timezone) : timezone;
        let options: { value: string, label: string }[] = [];

        // Format 1: New array format with key/label/value/selected
        if (Array.isArray(tzObj)) {
            options = (tzObj as Array<Record<string, unknown>>)
                .filter((item) => item['selected'] !== false) // Only show enabled timezones
                .map((item) => {
                    // New format: { key, label, value, selected }
                    if (item['key'] !== undefined) {
                        const isOffset = (s: string) => s && /^[-+]?[\d:.]+$/.test(s.toString());

                        let name = String(item['key']);
                        let offsetVal = '';

                        // Check if value is the offset (Standard)
                        if (item['value'] !== undefined && item['value'] !== null && isOffset(String(item['value']))) {
                            offsetVal = String(item['value']);
                            name = String(item['label'] ?? item['key']);
                        }
                        // Check if label is the offset (User's case: label="-12", value="Name")
                        else if (item['label'] !== undefined && item['label'] !== null && isOffset(String(item['label']))) {
                            offsetVal = String(item['label']);
                            name = (item['value'] && item['value'] !== offsetVal) ? String(item['value']) : String(item['key']);
                        }

                        // Format the GMT offset for display
                        let offsetStr = '';
                        if (offsetVal) {
                            if (offsetVal === '0' || offsetVal === '+0' || offsetVal === '+0:00' || offsetVal === '0:00') {
                                offsetStr = '(GMT)';
                            } else if (offsetVal.startsWith('-') || offsetVal.startsWith('+')) {
                                offsetStr = `(GMT${offsetVal})`;
                            } else {
                                offsetStr = `(GMT+${offsetVal})`;
                            }
                        }

                        // Store as "Offset|Name" format
                        const storedValue = offsetVal ? `${offsetVal}|${name}` : String(item['key']);

                        return {
                            value: storedValue,
                            label: `${name} ${offsetStr}`.trim()
                        };
                    }
                    // Legacy format: { "Africa/Abidjan": "Abidjan|0" }
                    const key = Object.keys(item)[0];
                    const rawValue = item[key];
                    let label = key.includes('/') ? key.split('/').pop() || key : key;
                    let offsetVal = '';

                    if (typeof rawValue === 'string') {
                        if (rawValue.includes('|')) {
                            const parts = rawValue.split('|');
                            const p0 = parts[0].trim();
                            const p1 = parts.length > 1 ? parts[1].trim() : '';
                            // Strict check for offset (numbers, colon, plus/minus only)
                            const isOffset = (s: string) => /^[-+]?[\d:.]+$/.test(s);

                            if (isOffset(p0) && !isOffset(p1)) {
                                // Format: Offset|Name
                                offsetVal = p0;
                                label = p1 || label;
                            } else {
                                // Format: Name|Offset (Standard Legacy)
                                label = p0 || label;
                                offsetVal = p1;
                            }
                        } else if (/^[-+]?\d/.test(rawValue)) {
                            // Check if rawValue looks like an offset (starts with number, +, or -)
                            offsetVal = rawValue;
                        }
                    }

                    let displayLabel = label;
                    if (offsetVal) {
                        displayLabel += (offsetVal === '0') ? ' (GMT)' : ` (GMT${offsetVal.startsWith('-') || offsetVal.startsWith('+') ? offsetVal : '+' + offsetVal})`;
                    }

                    const storedValue = offsetVal ? `${offsetVal}|${label}` : key;
                    return { value: storedValue, label: displayLabel };
                });
        }

        // Format 2: Object with key-value pairs (legacy)
        else if (typeof tzObj === 'object') {
            options = Object.keys(tzObj).map(key => {
                const rawValue = tzObj[key];
                let label = key.includes('/') ? key.split('/').pop() || key : key;
                let offsetVal = '';

                if (typeof rawValue === 'string') {
                    if (rawValue.includes('|')) {
                        const parts = rawValue.split('|');
                        if (parts[0]) label = parts[0];
                        if (parts[1]) offsetVal = parts[1];
                    } else if (/^[-+]?\d/.test(rawValue)) {
                        offsetVal = rawValue;
                    }
                }

                let displayLabel = label;
                if (offsetVal) {
                    displayLabel += (offsetVal === '0') ? ' (GMT)' : ` (GMT${offsetVal.startsWith('-') || offsetVal.startsWith('+') ? offsetVal : '+' + offsetVal})`;
                }

                const storedValue = offsetVal ? `${offsetVal}|${label}` : key;
                return { value: storedValue, label: displayLabel };
            });
        }

        return options;
    } catch (e) {
        console.warn('[Timezone] Failed to parse timezone field:', e);
        return [];
    }
}

/**
 * Helper function to extract language options from model
 */
export function extractLanguageOptions(language: unknown): { value: string, label: string }[] {
    if (!language) return [];
    try {
        const langObj = typeof language === 'string' ? JSON.parse(language) : language;
        if (Array.isArray(langObj)) {
            return (langObj as Array<Record<string,unknown>>)
                .filter((item) => item['selected'] !== false) // Only show enabled languages
                .map((item) => {
                    // New format: { code, name, selected }
                    if (item['code']) {
                        return {
                            value: String(item['code']),
                            label: String(item['name'] ?? item['code'])
                        };
                    }
                    // Alternative format: { value, label, selected }
                    if (item['value'] !== undefined) {
                        return {
                            value: String(item['value']),
                            label: String(item['label'] ?? item['value'])
                        };
                    }
                    // Legacy format check
                    const key = Object.keys(item)[0];
                    if (key && key !== 'selected' && key !== 'code' && key !== 'name' && key !== 'value' && key !== 'label') {
                        return { value: key, label: String(item[key]) };
                    }
                    return null;
                })
                .filter((x): x is { value: string, label: string } => x !== null);
        }
        if (typeof langObj === 'object') {
            const keys = Object.keys(langObj as object);
            return keys.map(key => ({
                value: String((langObj as Record<string,unknown>)[key]),
                label: key
            }));
        }
        return [];
    } catch (e) {
        console.warn('[Language] Failed to parse language field:', e);
        return [];
    }
}

/**
 * Helper to parse JSON options from model 
 * (assuming they are arrays of strings or objects {label, value})
 */
export function getOptions(jsonField: unknown): { label: string, value: string }[] {
    if (!jsonField) return [];

    // Parse string if needed
    let data: unknown = jsonField;
    if (typeof jsonField === 'string') {
        try {
            data = JSON.parse(jsonField);
        } catch (e) {
            console.warn('Failed to parse options JSON:', e);
            return [];
        }
    }

    // Handle array directly
    if (Array.isArray(data)) {
        return (data as Array<Record<string,unknown> | string>).map((item) => {
            if (typeof item === 'string') return { label: item, value: item };
            const rec = item as Record<string, unknown>;
            // Check for selected property
            if (rec['selected'] === false) return null;

            // Prioritize label/name for display, value/id/key for value
            const label = rec['label'] ?? rec['name'] ?? rec['key'] ?? rec['value'];
            const value = rec['value'] !== undefined ? rec['value'] : (rec['id'] ?? rec['key']);

            return {
                label: String(label),
                value: String(value)
            };
        }).filter((x): x is { label: string, value: string } => x !== null);
    }

    // Handle object with values property (e.g., { values: [...] })
    const dataObj = data as Record<string, unknown>;
    if (dataObj['values'] && (Array.isArray(dataObj['values']) || typeof dataObj['values'] === 'object')) {
        return getOptions(dataObj['values']);
    }

    // Handle standard object { "Label": "Value" } or { "Value": "Label" }
    if (typeof data === 'object') {
        return Object.entries(data as Record<string, unknown>).map(([key, value]) => {
            // Check for explicit object { value, label } inside the map
            if (typeof value === 'object' && value !== null) {
                const vRec = value as Record<string, unknown>;
                if (vRec['selected'] === false) return null;
                return {
                    label: String(vRec['label'] ?? vRec['name'] ?? key),
                    value: String(vRec['value'] !== undefined ? vRec['value'] : key)
                };
            }

            // Heuristic: If key looks like a readable label (spaces, CamelCase) and value is simple, treat key as label
            // Example: "12 Hour": "0" -> Label: "12 Hour", Value: "0"
            return {
                label: key,                 // Use key as label (e.g., "12 Hour")
                value: String(value)        // Use value as value (e.g., "0")
            };
        }).filter((x): x is { label: string, value: string } => x !== null);
    }

    return [];
}

export const extractPhonebookInfo = (phonebookModel: unknown) => {
    if (!phonebookModel) return { count: 0, fields: {} };

    let model: Record<string, unknown>;
    if (typeof phonebookModel === 'string') {
        try {
            model = JSON.parse(phonebookModel) as Record<string, unknown>;
        } catch (e) {
            console.error('Error parsing phonebook model', e);
            return { count: 0, fields: {} };
        }
    } else {
        model = phonebookModel as Record<string, unknown>;
    }

    // Check for remote_phonebooks structure
    const remotePhonebooks = model['remote_phonebooks'] as Record<string,unknown>|undefined;
    if (remotePhonebooks) {
        return {
            count: Number(remotePhonebooks['count']) || 0,
            fields: remotePhonebooks['fields'] || {},
            type: 'remote'
        };
    }

    // Check for xml_phonebooks structure
    const xmlPhonebooks = model['xml_phonebooks'] as Record<string,unknown>|undefined;
    if (xmlPhonebooks) {
        return {
            count: Number(xmlPhonebooks['count']) || 0,
            fields: xmlPhonebooks['fields'] || {},
            type: 'xml'
        };
    }

    // Fallback logic if needed (generic structure)
    if (model['count']) {
        return {
            count: Number(model['count']),
            fields: model['fields'] || {},
            type: 'generic'
        };
    }

    return { count: 0, fields: {} };
};
